/**
 * Exponential backoff with full jitter (1s → 30s × 2).
 *
 *   attempt 0 → [0,    initialMs)
 *   attempt 1 → [0,    initialMs * multiplier)
 *   ...up to [0, maxMs).
 *
 * Full jitter (Brakeman, AWS) avoids thundering-herd retries when many tabs
 * reconnect after a CSMS hiccup.
 */
export interface ReconnectStrategyOptions {
  readonly initialMs?: number;
  readonly maxMs?: number;
  readonly multiplier?: number;
  readonly random?: () => number;
}

export class ReconnectStrategy {
  readonly #initialMs: number;
  readonly #maxMs: number;
  readonly #multiplier: number;
  readonly #random: () => number;
  #attempt = 0;

  constructor(opts: ReconnectStrategyOptions = {}) {
    this.#initialMs = opts.initialMs ?? 1_000;
    this.#maxMs = opts.maxMs ?? 30_000;
    this.#multiplier = opts.multiplier ?? 2;
    this.#random = opts.random ?? Math.random;
  }

  /** Delay (ms) before the next attempt. Advances the attempt counter. */
  next(): number {
    const baseline = Math.min(this.#maxMs, this.#initialMs * this.#multiplier ** this.#attempt);
    this.#attempt++;
    return Math.floor(this.#random() * baseline);
  }

  /** Call on a successful (re)connect to reset the backoff. */
  reset(): void {
    this.#attempt = 0;
  }

  get attempt(): number {
    return this.#attempt;
  }
}
